
import { Calendar, ClipboardList, Target, TrendingUp, Heart, Users, Activity, Star, Zap, CheckCircle, ArrowRight } from "lucide-react";
import { usePatientJourney } from "@/hooks/usePatientJourney";
import { usePageContent } from "@/hooks/usePageContent";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const iconMap = { Calendar, ClipboardList, Target, TrendingUp, Heart, Users, Activity, Star, Zap, CheckCircle };

const fallbackSteps = [
  { id: "fallback-evaluation", icon: "Calendar", title: "Avaliação gratuita", description: "Começamos com uma conversa. Entendo seus objetivos, histórico alimentar e desafios antes de qualquer prescrição." },
  { id: "fallback-diagnosis", icon: "ClipboardList", title: "Diagnóstico completo", description: "Análise da sua composição corporal, exames e estilo de vida para criar um plano baseado na sua realidade." },
  { id: "fallback-plan", icon: "Target", title: "Plano alimentar personalizado", description: "Cardápio adaptado ao que você gosta, à sua rotina e ao seu orçamento. Sem proibições desnecessárias." },
  { id: "fallback-followup", icon: "TrendingUp", title: "Acompanhamento contínuo", description: "Consultas de retorno regulares para ajustar o plano, celebrar resultados e superar os momentos difíceis." },
  { id: "fallback-result", icon: "Heart", title: "Resultado sustentável", description: "Mais do que emagrecer: você aprende a se alimentar bem para a vida toda, com saúde e prazer." },
];

export function PatientJourney({ className = "bg-[#f8fdf9]" }: { className?: string }) {
  const { steps: apiSteps, isLoading } = usePatientJourney();
  const steps = apiSteps.length > 0 ? apiSteps : fallbackSteps;
  const { getText } = usePageContent('home');
  const { getSetting } = useSiteSettings();
  let ctaLinks: Record<string, { label?: string; url?: string }> = {};
  try {
    const parsed = JSON.parse(getSetting('patient_journey_cta_links', '{}'));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ctaLinks = parsed;
  } catch { /* Keep links empty when the setting is invalid. */ }

  const sectionLabel = getText('jornada', 'label',    'Como funciona');
  const title        = getText('jornada', 'title',    'Sua jornada, passo a passo');
  const subtitle     = getText('jornada', 'subtitle', 'Um processo claro, humano e sem surpresas. Do primeiro contato até o resultado real.');

  return (
    <section id="jornada" className={`py-24 ${className}`}>
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label mb-3">{sectionLabel}</p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-brand-primary font-primary leading-tight mb-4">
            {title}
          </h2>
          <p className="text-gray-500 text-lg font-primary font-light max-w-lg mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Timeline */}
        {isLoading ? (
          <div className="max-w-3xl mx-auto space-y-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Linha vertical */}
              <div className="absolute left-8 top-0 bottom-0 w-px bg-brand-primary/15 hidden sm:block" />

              <div className="space-y-4">
                {steps.map((step, index) => {
                  const IconComponent = iconMap[step.icon as keyof typeof iconMap] || Calendar;
                  const isLast = index === steps.length - 1;
                  const stepLink = ctaLinks[step.id];
                  const isSafeLink = !!stepLink?.url && (/^https?:\/\//i.test(stepLink.url) || stepLink.url.startsWith('/'));
                  return (
                    <div key={index} className="relative flex gap-6 sm:pl-0 pl-4">
                      {/* Número / Ícone */}
                      <div className="relative flex-shrink-0 z-10">
                        <div className={`w-16 h-16 rounded-2xl flex flex-col items-center justify-center shadow-sm border-2 transition-colors ${
                          isLast
                            ? "bg-brand-secondary border-brand-secondary text-white"
                            : "bg-white border-brand-primary/20 text-brand-primary"
                        }`}>
                          <IconComponent className="w-5 h-5 mb-0.5" />
                          <span className={`text-xs font-bold font-primary ${isLast ? "text-white/80" : "text-brand-primary/60"}`}>
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        </div>
                        {/* Conector */}
                        {!isLast && (
                          <div className="absolute top-full left-1/2 -translate-x-1/2 w-px h-4 bg-brand-primary/15 hidden sm:block" />
                        )}
                      </div>

                      {/* Conteúdo */}
                      <div className={`flex-1 bg-white rounded-2xl border p-6 shadow-sm hover:shadow-md transition-all duration-300 mb-4 ${
                        isLast ? "border-brand-secondary/30" : "border-gray-100"
                      }`}>
                        <h3 className={`font-bold font-primary text-base mb-1.5 ${isLast ? "text-brand-secondary" : "text-gray-800"}`}>
                          {step.title}
                        </h3>
                        <p className="text-gray-500 text-sm font-primary font-light leading-relaxed">
                          {step.description}
                        </p>
                        {isSafeLink && (
                          <a
                            href={stepLink.url!}
                            target={/^https?:\/\//i.test(stepLink.url!) ? '_blank' : undefined}
                            rel={/^https?:\/\//i.test(stepLink.url!) ? 'noopener noreferrer' : undefined}
                            className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-brand-primary hover:underline"
                          >
                            {stepLink.label || 'Veja aqui uma avaliação de caso'}
                            <ArrowRight className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA embaixo */}
            <div className="text-center mt-10">
              <button
                onClick={() => window.open("https://wa.me/5511998874081", "_blank")}
                className="bg-brand-primary hover:bg-brand-primary/90 text-white font-bold font-primary rounded-full px-10 py-4 text-base transition-all duration-200 shadow-lg shadow-brand-primary/25 hover:scale-105 inline-flex items-center gap-2"
              >
                Quero começar minha jornada →
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
